<script setup>
import LoginInputEmail from "@/pages/Login/LoginInputEmail.vue";
import {computed, onBeforeMount, ref} from 'vue'
import stringUtils from "@/service/stringUtils";
import LoginInputPhone from "@/pages/Login/LoginInputPhone.vue";
import {useStore} from "vuex";
import {AuthApi} from "@/service/authApi";
import {fpPromise, SITE_URL} from "@/service/config";
import { createToaster } from "@meforma/vue-toaster";
import {useRoute, useRouter} from "vue-router";
import SubtractSvg from "@/components/UI/svg/SubtractSvg.vue";
import TergumLogo from "@/components/UI/svg/TergumLogo.vue";
import OauthButtonVK from "@/components/UI/OauthButtonVK.vue";

const toaster = createToaster({ /* options */ });

const store = useStore();

const router = useRouter()
const route = useRoute()

const yandexRetries = ref(0)


const registerPassword = ref("")

const showPassword = ref(false)


const isPrivacyPolicyAccepted = ref(false);

const registrationStep = ref(1);

const userEmail = ref("");

const userName = ref("");

const phoneNumber = ref("")

const isLoading = ref(false);


const isPasswordHasCapital = computed(() => {
    return stringUtils.hasCapital(registerPassword.value)
})

const isPasswordHasLowerCase = computed(() => {
    return stringUtils.hasLowerCase(registerPassword.value)
})

const isPasswordHasDigits = computed(() => {
    return stringUtils.hasDigits(registerPassword.value)
})

const isPasswordHasSpecialChars = computed(() => {
    return stringUtils.hasSpecialChars(registerPassword.value)
})

const isPasswordLengthValid = computed(() => {
    return (registerPassword.value.length >= 8) && (registerPassword.value.length <= 32)
})

const isAllRulesTrue = computed(() => {
    return (
        isPasswordHasCapital.value &&
        isPasswordHasLowerCase.value &&
        isPasswordHasDigits.value &&
        isPasswordHasSpecialChars.value &&
        isPasswordLengthValid.value &&
        isPrivacyPolicyAccepted.value
    )
})


async function loadYandexAuth() {
    initializeYandexAuth()
}

function initializeYandexAuth() {
    try {
        window.YaAuthSuggest.init(
            {
                client_id: '113c1c0e05cb437dab7a17f2836af2fc',
                response_type: 'token',
                redirect_uri: SITE_URL + '/yandex/oauth',
            },
            SITE_URL + '/yandex/oauth',
            {
                view: "button",
                parentId: "buttonContainerId",
                buttonSize: 'm',
                buttonView: 'main',
                buttonTheme: 'light',
                buttonBorderRadius: "5",
                buttonIcon: 'ya',
            }
        )
            .then(function(result) {
                return result.handler()
            })
            .then(data => console.log('Сообщение с токеном', data))
            .catch(error => {
                console.log('Yandex OAuth error:', error);
                if (error.code === 'in_progress') {
                    return
                }
                if (yandexRetries.value < 3) {
                    yandexRetries.value += 1
                    initializeYandexAuth();
                }
            });
    } catch (error) {
        console.warn('Yandex OAuth initialization failed:', error);
    }
    window.reload_self = () => {
        window.location.reload();
    }
}

onBeforeMount(async () => {
    await loadYandexAuth()
})

async function generateFingerPrint() {
    const fp = await fpPromise
    const result = await fp.get()
    return result.visitorId
}

function togglePassword() {
    showPassword.value = !showPassword.value
}

function setError(message) {
    toaster.error(message, {
        position: "bottom-right",
        duration: 10000,
        max: 12,
    })
}


async function checkIfEmailInUse(email) {
    isLoading.value = true
    let result = await AuthApi.checkIfEmailInUse(email)
    if (result.status === "error") {
        setError(result.message)
        return
    }
    isLoading.value = false
    return result.value
}

function validateEmail(email) {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
}

async function register() {
    isLoading.value = true
    let fingerprint = await generateFingerPrint()
    let registerData = await AuthApi.register(
        userEmail.value,
        registerPassword.value,
        fingerprint,
        userName.value,
        store.state.register.reg.phoneNumber,
        "BY_PASSWORD"
    )

    if (registerData.status === "error") {
        setError(registerData.message)
        userEmail.value = ""
        registerPassword.value = ""
        phoneNumber.value = ""
        userName.value = ""
        isPrivacyPolicyAccepted.value = false
        registrationStep.value = 1
        return
    }

    localStorage.setItem("isUserConfirmed", "false")

    isLoading.value = false
    await router.push({name: "CodeAcception", query: {...route.query}})
}


async function toStepTwo() {
    if (!validateEmail(userEmail.value)) {
        setError("Значение «Email» не является правильным email адресом.")
        return
    }
    if (!checkIfEmailInUse(userEmail.value)) {
        setError("Email уже используется")
        return
    }
    registrationStep.value = 2
}

</script>

<template>
    <div class="min-h-screen flex flex-col md:flex-row bg-gray-50">
        <div class="relative w-full hidden xs:hidden sm:hidden md:block md:w-1/2 min-h-[40vh] md:min-h-screen bg-gradient-to-br overflow-hidden from-blue-50 to-blue-100">
            <SubtractSvg class="absolute w-full h-full scale-[1.7]" />
            <div class="absolute w-full z-10 flex flex-col items-center justify-center h-full p-8">
                <div class="w-full max-w-xs bg-white flex items-center justify-center py-2 px-5 rounded-xl">
                    <TergumLogo />
                </div>
            </div>
        </div>

        <div class="w-full md:w-1/2 h-screen flex items-center justify-center">
            <div
                class="flex flex-col rounded-2xl bg-white items-center justify-center shadow-xl h-[600px] w-[340px]
                    sm:w-[400px]
                    xl:w-[450px]
                "
            >
                <div class="w-3/4 text-lg flex font-bold" v-if="registrationStep === 1">
                    Зарегистрируйте аккаунт
                </div>
                <div class="w-3/4 text-lg flex font-bold" v-if="registrationStep === 2">
                    Шаг 2
                </div>
                <div class="flex w-3/4 flex-col">
                    <form class="flex flex-col" v-if="registrationStep === 1">
                        <LoginInputEmail v-model="userEmail"/>
                        <div class="w-full relative mt-5">
                            <input
                                :type="showPassword ? 'text' : 'password'"
                                id="password"
                                v-model="registerPassword"
                                class="h-10 w-full px-3 pt-1 text-xs border-2 border-transparent rounded-md bg-light-blue outline-none focus:outline-none focus:border-blue-600 password-criterias"
                            >
                            <label class="absolute top-1 left-3 transparent small-text-label select-none">Пароль</label>
                            <!-- svg глазика для того чтобы скрыть пароль -->
                            <div v-if="!showPassword" class="absolute right-3 top-3 cursor-pointer" v-on:click="togglePassword">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                    <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <!-- svg глазика для того чтобы показать пароль -->
                            <div v-else class="absolute right-3 top-3 cursor-pointer" v-on:click="togglePassword">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                    <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                                    <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                                    <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                                </svg>
                            </div>
                        </div>
                        <div class="mt-2">
                            <div class="text-xs font-bold">
                                Требования к паролю:
                            </div>
                            <div class="text-xs">
                                <div :class="{'text-green-600': isPasswordHasCapital && isPasswordHasLowerCase, 'text-red-500': !isPasswordHasCapital || !isPasswordHasLowerCase}">
                                    • прописные и строчные буквы латинского алфавита (A-Z)
                                </div>
                                <div :class="{'text-green-600': isPasswordHasDigits, 'text-red-500': !isPasswordHasDigits}">
                                    • хотя бы одна цифра (0-9)
                                </div>
                                <div :class="{'text-green-600': isPasswordHasSpecialChars, 'text-red-500': !isPasswordHasSpecialChars}">
                                • хотя бы один спецсимвол (!@#$% и др.)
                                </div>
                                <div :class="{'text-green-600': isPasswordLengthValid, 'text-red-500': !isPasswordLengthValid}">
                                • длина от 8 до 48 символов включительно
                                </div>
                            </div>
                        </div>
                        <div class="mt-5 flex">
                            <div>
                                <input type="checkbox" id="checkbox" v-model="isPrivacyPolicyAccepted" class="checkbox">
                            </div>
                            <div class="ml-5 flex items-center">
                                <div class="text-xs">
                                    <p class="m-0">
                                        Я принимаю условия <a target="_blank" href="https://tergum.ru/confidential" class="underline">политики конфиденциальности</a> и <a target="_blank" href="https://tergum.ru/offer" class="underline">пользовательского соглашения</a>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div v-if="!isAllRulesTrue" class="h-10 w-full bg-gray-400 text-white text-sm flex items-center justify-center select-none mt-10 rounded-md cursor-not-allowed">
                            РЕГИСТРАЦИЯ
                        </div>
                        <div v-else v-on:click="toStepTwo" class="h-10 w-full bg-blue-600 text-white text-sm flex items-center justify-center select-none cursor-pointer mt-10 hover:bg-blue-700 rounded-md">
                            РЕГИСТРАЦИЯ
                        </div>
                    </form>
                    <div id="buttonContainerId" class="my-2" v-if="registrationStep === 1"></div>
                    <OauthButtonVK v-if="registrationStep === 1" />

                    <form class="flex flex-col" v-if="registrationStep === 2">
                        <div class="text-sm">
                            Вы регистрируетесь как <span class="text-sm font-bold">{{userEmail}}</span>
                        </div>
                        <div
                            class="w-full relative mt-5 flex border-2 border-transparent rounded-md pass-form"
                        >
                            <div class="relative w-full">
                                <input
                                    v-model="userName"
                                    class="block h-10 w-full text-xs bg-light-blue outline-none rounded-md pt-1 px-3"
                                />
                            </div>
                            <label class="absolute top-1 left-3 transparent small-text-label select-none">Имя</label>
                            <div class="absolute right-3 top-3">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                    <path fill-rule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clip-rule="evenodd" />
                                </svg>
                            </div>
                        </div>
                        <LoginInputPhone v-model:phone="phoneNumber"/>
                        <div v-if="!isAllRulesTrue" class="mt-10 h-10 w-full bg-gray-400 text-white text-sm flex items-center justify-center select-none rounded-md cursor-not-allowed">
                            РЕГИСТРАЦИЯ
                        </div>
                        <div v-else v-on:click="register" class="mt-10 h-10 w-full bg-blue-600 text-white text-sm flex items-center justify-center select-none cursor-pointer hover:bg-blue-700 rounded-md">
                            <div v-if="!isLoading">
                                РЕГИСТРАЦИЯ
                            </div>
                            <div v-else>
                                <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        </div>
                    </form>

                    <div class="mt-3 flex mx-auto">
                        <div>У вас уже есть аккаунт?</div>
                        <router-link to="/login" class="font-bold text-sm text-blue-500 ml-1">Войти</router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bg-light-blue {
    background-color: #dee8f5;
}
.small-text-label {
    font-size: 0.5rem
}
.password-criterias-display{
    display: none;
}
.password-criterias:focus ~ .password-criterias-display{
    display: flex;
}
.arrow-down {
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;

    border-left: 8px solid #dee8f5;
}
.bg-light-block {
    background: #dee8f5;
}
.checkbox {
    appearance: none;
    background-color: #dee8f5;;
    margin: 0;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    display: grid;
    place-content: center;
    user-select: none;
    cursor: pointer;
}
.checkbox::before {
    content: "";
    width: 7px;
    height: 7px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em #0A84FF;
}
.checkbox:checked::before {
    transform: scale(1);
}
.checkbox:checked {
    border-color: #0A84FF;
}

</style>